/**
 * PassportCard - A passport-styled card for displaying trip information.
 *
 * Features:
 * - Passport cover with gradient background
 * - Gold emblem/logo
 * - Trip title, destination, dates
 * - From city
 * - Travel stamps overlay
 * - Hover shine effect
 */

import { useMemo } from 'react'
import { getPassportColors, getPassportStamps } from '@/lib/colors'
import { formatProtoDate } from '@/lib/date'
import { PASSPORT_CARD } from '@/overlays/constants/dimensions'
import { FONT_STYLES } from '@/styles/fonts'
import type { Trip as TripMessage } from '@generated/proto/model'

interface PassportCardProps {
  /** Trip data to display */
  trip: TripMessage
  /** Trip ID for color generation */
  tripId: string
  /** User's departure city */
  departureCity?: string
  /** Click handler */
  onClick?: () => void
  /** Mouse enter handler (for hover tracking) */
  onMouseEnter?: () => void
  /** Mouse leave handler (for hover tracking) */
  onMouseLeave?: () => void
}

/**
 * A passport-styled card displaying trip information.
 *
 * @example
 * ```tsx
 * <PassportCard
 *   trip={trip}
 *   tripId={tripId}
 *   departureCity="San Francisco"
 *   onClick={() => navigate(`/trips/${tripId}`)}
 * />
 * ```
 */
export function PassportCard({
  trip,
  tripId,
  departureCity = 'San Francisco',
  onClick,
  onMouseEnter,
  onMouseLeave,
}: PassportCardProps) {
  // Get destination city from the first destination's location
  const firstDestination = trip.destinations?.[0]
  const destinationCity = firstDestination?.location || firstDestination?.title || 'Unknown'

  // Format dates using common utility
  const departureDate = formatProtoDate(trip.departureDate) ?? '—'
  const returnDate = formatProtoDate(trip.returnDate) ?? '—'

  // Get unique passport colors and stamps (memoized for performance)
  const passportColors = useMemo(() => getPassportColors(tripId), [tripId])
  const stamps = useMemo(() => getPassportStamps(tripId), [tripId])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick?.()
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      aria-label={`View trip: ${trip.title || 'Untitled Trip'} to ${destinationCity}`}
      className="flex-shrink-0 snap-start cursor-pointer group passport-card text-left"
    >
      {/* Passport Card */}
      <div
        className="relative rounded-lg shadow-lg overflow-hidden transition-transform duration-300 origin-bottom group-hover:scale-110 group-hover:shadow-2xl"
        style={{
          width: PASSPORT_CARD.WIDTH,
          height: PASSPORT_CARD.HEIGHT,
          background: `linear-gradient(145deg, ${passportColors.coverLight} 0%, ${passportColors.cover} 50%, ${passportColors.coverDark} 100%)`,
        }}
      >
        {/* Shine effect overlay */}
        <div
          className="shine-effect absolute inset-0 w-1/2 h-full pointer-events-none z-20"
          style={{
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
            left: '-100%',
          }}
        />

        {/* Leather texture overlay */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Gold border inset */}
        <div
          className="absolute inset-3 border rounded-lg pointer-events-none"
          style={{ borderColor: `${passportColors.gold}40` }}
        />

        {/* Decorative corner flourishes */}
        <div className="absolute top-4 left-4 w-5 h-5 border-t-2 border-l-2" style={{ borderColor: passportColors.gold }} />
        <div className="absolute top-4 right-4 w-5 h-5 border-t-2 border-r-2" style={{ borderColor: passportColors.gold }} />
        <div className="absolute bottom-4 left-4 w-5 h-5 border-b-2 border-l-2" style={{ borderColor: passportColors.gold }} />
        <div className="absolute bottom-4 right-4 w-5 h-5 border-b-2 border-r-2" style={{ borderColor: passportColors.gold }} />

        {/* Passport content */}
        <div className="relative h-full flex flex-col p-5">
          {/* Header - "PASSPORT" text */}
          <div className="text-center mb-2">
            <p
              className="text-xs uppercase tracking-[0.3em] opacity-80"
              style={{ ...FONT_STYLES.alga, color: passportColors.gold }}
            >
              Passport
            </p>
          </div>

          {/* Emblem/Logo area */}
          <div className="flex justify-center mb-4">
            <div
              className="w-14 h-14 rounded-full border-2 flex items-center justify-center"
              style={{ borderColor: passportColors.gold }}
            >
              <svg
                className="w-8 h-8"
                viewBox="0 0 24 24"
                fill="none"
                stroke={passportColors.gold}
                strokeWidth="1.5"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                <path d="M2 12h20" />
              </svg>
            </div>
          </div>

          {/* Trip Title */}
          <div className="text-center mb-3">
            <h3
              className="text-lg font-bold uppercase tracking-wider leading-tight"
              style={{
                ...FONT_STYLES.pioneer,
                color: passportColors.gold,
                textShadow: `0 1px 2px ${passportColors.coverDark}`,
              }}
            >
              {trip.title || 'Untitled Trip'}
            </h3>
          </div>

          {/* Destination */}
          <div className="text-center mb-4">
            <p
              className="text-[10px] uppercase tracking-wider mb-1 opacity-70"
              style={{ ...FONT_STYLES.alga, color: passportColors.textMuted }}
            >
              Destination
            </p>
            <p
              className="text-xl font-bold uppercase tracking-widest"
              style={{
                ...FONT_STYLES.pioneer,
                color: passportColors.gold,
                textShadow: `0 1px 2px ${passportColors.coverDark}`,
              }}
            >
              {destinationCity}
            </p>
          </div>

          {/* Dates - integrated in gold */}
          <div className="text-center mb-4">
            <p
              className="text-sm tracking-wider"
              style={{
                ...FONT_STYLES.alga,
                color: passportColors.gold,
              }}
            >
              {departureDate} — {returnDate}
            </p>
          </div>

          {/* From city - at bottom */}
          <div className="mt-auto text-center">
            <p
              className="text-[10px] uppercase tracking-wider mb-1 opacity-70"
              style={{ ...FONT_STYLES.alga, color: passportColors.textMuted }}
            >
              From
            </p>
            <p
              className="text-sm font-bold uppercase tracking-widest"
              style={{
                ...FONT_STYLES.pioneer,
                color: passportColors.gold,
                textShadow: `0 1px 2px ${passportColors.coverDark}`,
              }}
            >
              {departureCity}
            </p>
          </div>

          {/* Travel stamps overlay */}
          {stamps.map((stamp, i) => (
            <div
              key={i}
              className="absolute pointer-events-none opacity-25"
              style={{
                left: `${stamp.x}%`,
                top: `${stamp.y}%`,
                transform: `rotate(${stamp.rotation}deg) scale(${stamp.scale * 0.8})`,
              }}
            >
              {/* Circular stamp */}
              <div
                className="w-12 h-12 rounded-full border-2 flex items-center justify-center"
                style={{ borderColor: stamp.color }}
              >
                <div
                  className="text-[6px] font-bold uppercase tracking-wider text-center leading-tight"
                  style={{ color: stamp.color, ...FONT_STYLES.alga }}
                >
                  {destinationCity.slice(0, 3)}
                  <br />
                  <span className="text-[5px]">&#9992;</span>
                </div>
              </div>
            </div>
          ))}

          {/* Rectangle stamp */}
          <div
            className="absolute pointer-events-none opacity-20"
            style={{
              right: '10%',
              bottom: '30%',
              transform: 'rotate(-8deg)',
            }}
          >
            <div
              className="px-3 py-1 border-2 rounded-sm"
              style={{ borderColor: passportColors.stamp }}
            >
              <p
                className="text-[7px] font-bold uppercase tracking-widest"
                style={{ color: passportColors.stamp, ...FONT_STYLES.pioneer }}
              >
                APPROVED
              </p>
            </div>
          </div>
        </div>
      </div>
    </button>
  )
}
